/**
 * ThinSlim filter rail behaviour.
 *
 * Everything here is an enhancement. With scripting off the rail is still a
 * plain GET form with an Apply button, every chip is still a submit button in
 * its own form, and Load more is still a link to the next page.
 *
 * What this adds:
 *   - filtering in place: a change fetches only this section and swaps the
 *     results in, with the URL and history kept in step, so the page neither
 *     reloads nor jumps to the top
 *   - collapsible groups that remember their open state for the visit
 *   - brand type-ahead and the Show all reveal
 *   - the mobile bottom sheet, including focus capture and restore
 *   - Load more, which fetches the next page and appends its cards
 */
(() => {
  const DESKTOP = window.matchMedia('(min-width: 990px)');

  document.documentElement.classList.add('tsf-facets-js');

  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  /* ------------------------------------------- filters Shopify left out */

  /**
   * collection.filters caps each filter at 100 values, and a value past that
   * cap is missing from filter.values and from filter.active_values even
   * while it is the filter in force. Shopify still applies it, so the grid is
   * right, but nothing server-rendered can show it as applied: the checkbox
   * comes back unchecked and no chip is drawn.
   *
   * The query string does know, and Liquid cannot read it. So the applied
   * values are restored here from location.search: the box is ticked, an
   * option hidden behind "Show all" is lifted to the top of its group, and a
   * chip is drawn to match the ones Liquid drew. Each chip is the same GET
   * form carrying every other active parameter, so removing it behaves
   * exactly like removing a server-rendered one.
   */
  function restoreCappedFilters(rail) {
    let params;
    try {
      params = new URLSearchParams(window.location.search);
    } catch (error) {
      return;
    }
    if (!params.has('filter.p.vendor') && !params.has('filter.p.product_type')) return;

    const restored = [];
    rail.querySelectorAll('[data-facet-option] input[type="checkbox"]').forEach((input) => {
      if (input.checked || !input.name) return;
      if (!params.getAll(input.name).includes(input.value)) return;
      input.checked = true;
      restored.push(input);
    });
    if (restored.length === 0) return;

    restored.forEach((input) => {
      const item = input.closest('[data-facet-option]');
      if (!item || !item.classList.contains('tsf-facet__item--extra')) return;
      /* An applied filter is never hidden behind a reveal. */
      item.classList.remove('tsf-facet__item--extra');
      const list = item.closest('[data-facet-list]');
      if (list) list.prepend(item);
    });

    drawMissingChips(restored, params);
  }

  function chipsList() {
    const existing = document.querySelector('[data-tsf-chips] .tsf-chips__list');
    if (existing) return existing;

    /* Liquid renders the chip row inside the head slot, under the facet bar.
       When nothing was active there is no row at all, so one is built in the
       same place. */
    const slot = document.querySelector('.tsf-facets-head-slot');
    if (!slot) return null;

    const wrap = document.createElement('div');
    wrap.className = 'tsf-chips';
    wrap.setAttribute('data-tsf-chips', '');
    const heading = document.createElement('h2');
    heading.className = 'tsf-chips__title';
    heading.id = 'TsfChipsRestored';
    heading.textContent = 'Active filters';
    const list = document.createElement('ul');
    list.className = 'tsf-chips__list';
    list.setAttribute('role', 'list');
    list.setAttribute('aria-labelledby', heading.id);
    wrap.append(heading, list);
    slot.append(wrap);
    return list;
  }

  function drawMissingChips(inputs, params) {
    const list = chipsList();
    if (!list) return;

    inputs.forEach((input) => {
      const group = input.closest('[data-facet-group]');
      const facetName = group && group.getAttribute('data-facet-group') === 'brand' ? 'Brand' : 'Category';
      const nameNode = input.parentElement.querySelector('.tsf-facet__name');
      const label = nameNode ? nameNode.textContent.trim() : input.value;

      const item = document.createElement('li');
      item.className = 'tsf-chips__item';

      const chip = document.createElement('form');
      chip.className = 'tsf-chip';
      chip.method = 'get';
      chip.action = window.location.pathname;

      /* Every parameter except the one this chip drops. */
      const seen = new Set();
      params.forEach((value, key) => {
        if (key === input.name && value === input.value) return;
        if (seen.has(key + '\u0000' + value)) return;
        seen.add(key + '\u0000' + value);
        const hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = key;
        hidden.value = value;
        chip.append(hidden);
      });

      const text = document.createElement('span');
      text.className = 'tsf-chip__label';
      const facet = document.createElement('span');
      facet.className = 'tsf-chip__facet';
      facet.textContent = facetName;
      text.append(facet, document.createTextNode(' ' + label));

      const remove = document.createElement('button');
      remove.className = 'tsf-chip__remove';
      remove.type = 'submit';
      const removeText = document.createElement('span');
      removeText.className = 'visually-hidden';
      removeText.textContent = 'Remove filter ' + facetName + ': ' + label;
      const cross = document.createElement('span');
      cross.setAttribute('aria-hidden', 'true');
      cross.innerHTML = '&times;';
      remove.append(removeText, cross);

      chip.append(text, remove);
      item.append(chip);
      list.append(item);
    });
  }

  /* ------------------------------------------------ open / closed groups */

  const OPEN_KEY = 'tsf-facet-open';

  function readOpenState() {
    try {
      return JSON.parse(window.sessionStorage.getItem(OPEN_KEY) || '{}') || {};
    } catch (error) {
      return {};
    }
  }

  function writeOpenState(state) {
    try {
      window.sessionStorage.setItem(OPEN_KEY, JSON.stringify(state));
    } catch (error) {
      /* Storage can be blocked; the groups still work, they just forget. */
    }
  }

  function initGroups(rail) {
    const saved = readOpenState();
    rail.querySelectorAll('details[data-facet-group]').forEach((group) => {
      const key = group.getAttribute('data-facet-group');
      const checked = group.querySelectorAll('input[type="checkbox"]:checked').length;
      const hasActive = checked > 0 || !!group.querySelector('.tsf-facet__active');
      /* A group with an applied value is never folded away on load. The
         capped-filter restore can tick a box Liquid never saw, so its badge
         is drawn here when Liquid did not draw one. */
      if (hasActive) {
        group.open = true;
        const summary = group.querySelector('.tsf-facet__summary');
        if (checked > 0 && summary && !summary.querySelector('.tsf-facet__active')) {
          const badge = document.createElement('span');
          badge.className = 'tsf-facet__active tsf-num';
          badge.textContent = String(checked);
          const hidden = document.createElement('span');
          hidden.className = 'visually-hidden';
          hidden.textContent = ' applied';
          badge.append(hidden);
          const chevron = summary.querySelector('.tsf-facet__chevron');
          summary.insertBefore(badge, chevron);
        }
      } else if (key in saved) {
        group.open = saved[key];
      }
      group.addEventListener('toggle', () => {
        const state = readOpenState();
        state[key] = group.open;
        writeOpenState(state);
      });
    });
  }

  /* --------------------------------------------------- filtering in place */

  let inflight = null;
  let pending = 0;

  /* Section responses by URL. Hovering an option starts its request, so by
     the time the click lands the result is usually already here. */
  const sectionCache = new Map();
  const CACHE_LIMIT = 24;

  function sectionRequest(url, sectionId) {
    const request = new URL(url, window.location.origin);
    request.searchParams.set('section_id', sectionId);
    return request.toString();
  }

  function fetchSection(url, sectionId) {
    const key = sectionRequest(url, sectionId);
    if (sectionCache.has(key)) return sectionCache.get(key);
    const promise = fetch(key).then((response) => {
      if (!response.ok) throw new Error(String(response.status));
      return response.text();
    });
    promise.catch(() => sectionCache.delete(key));
    sectionCache.set(key, promise);
    if (sectionCache.size > CACHE_LIMIT) {
      sectionCache.delete(sectionCache.keys().next().value);
    }
    return promise;
  }

  // Keep presentation and search context while form controls replace filters.
  // Do not copy page or section_id: a changed filter starts on page one.
  function contextUrl(value) {
    const target = new URL(value, window.location.origin);
    const current = new URL(window.location.href);
    for (const key of ['view', 'brand', 'pairing', 'q', 'type', 'options[prefix]']) {
      if (!target.searchParams.has(key) && current.searchParams.has(key)) {
        current.searchParams.getAll(key).forEach(value => target.searchParams.append(key, value));
      }
    }
    target.searchParams.delete('page');
    target.searchParams.delete('section_id');
    return target;
  }

  function compatibleContext(target) {
    const params = target.searchParams;
    const vendors = params.getAll('filter.p.vendor').flatMap(value => value.split(','));
    if (vendors.length !== 1 || vendors[0] !== 'HealthSmart Foods') params.delete('brand');
    const pairings = {
      bread: ['Fruit Spreads, Jams & Jellies', 'Nut Butters', 'Dips & Spreads'],
      pita: ['Condiments & Salad Dressings', 'Packaged Meals & Side Dishes', 'Spices & Seasonings'],
    };
    const required = pairings[params.get('pairing')];
    // The category values can contain commas, so compare serialized choices.
    const types = params.getAll('filter.p.product_type');
    if (!required || !(types.length === 1 && types[0] === required.join(',')) &&
        !(types.length === required.length && required.every(value => types.includes(value)))) {
      params.delete('pairing');
    }
    return target;
  }

  function formUrl(form) {
    const target = contextUrl(form.getAttribute('action') || window.location.pathname);
    const params = target.searchParams;
    for (const key of [...params.keys()]) {
      if (key.startsWith('filter.') || key === 'sort_by' || key === 'page') params.delete(key);
    }
    const sort = document.querySelector('[data-facets-sort]');
    const defaultSort = sort ? sort.getAttribute('data-default-sort') : null;
    new FormData(form).forEach((value, key) => {
      if (typeof value !== 'string' || value.trim() === '') return;
      if (key === 'sort_by' && value === defaultSort) return;
      if (key.startsWith('filter.')) params.append(key, value);
      else params.set(key, value);
    });
    compatibleContext(target);
    return target.pathname + target.search + target.hash;
  }

  function captureState(root) {
    const rail = root.querySelector('[data-tsf-facets]');
    const panel = rail && rail.querySelector('[data-facets-panel]');
    const active = document.activeElement;
    const state = {
      panelScroll: panel ? panel.scrollTop : 0,
      focusId: active && root.contains(active) && active.id ? active.id : null,
      groups: {},
    };
    if (rail) {
      rail.querySelectorAll('details[data-facet-group]').forEach((group) => {
        const key = group.getAttribute('data-facet-group');
        const list = group.querySelector('[data-facet-list]');
        const search = group.querySelector('[data-facet-search]');
        state.groups[key] = {
          open: group.open,
          expanded: group.classList.contains('is-expanded'),
          listScroll: list ? list.scrollTop : 0,
          term: search ? search.value : '',
        };
      });
    }
    return state;
  }

  function restoreState(root, state) {
    const rail = root.querySelector('[data-tsf-facets]');
    if (!rail) return;
    rail.querySelectorAll('details[data-facet-group]').forEach((group) => {
      const saved = state.groups[group.getAttribute('data-facet-group')];
      if (!saved) return;
      const hasActive = !!group.querySelector('input:checked');
      group.open = saved.open || hasActive;
      if (saved.expanded) {
        const toggle = group.querySelector('[data-facet-toggle]');
        if (toggle) toggle.click();
      }
      const search = group.querySelector('[data-facet-search]');
      if (search && saved.term) {
        search.value = saved.term;
        search.dispatchEvent(new Event('input'));
      }
      const list = group.querySelector('[data-facet-list]');
      if (list) list.scrollTop = saved.listScroll;
    });
    const panel = rail.querySelector('[data-facets-panel]');
    if (panel) panel.scrollTop = state.panelScroll;
    if (state.focusId) {
      const target = document.getElementById(state.focusId);
      if (target) target.focus({ preventScroll: true });
    }
  }

  async function renderResults(root, url, { push = true } = {}) {
    const sectionId = root.getAttribute('section-id');
    if (!sectionId || !window.fetch || !window.DOMParser) {
      window.location.assign(url);
      return;
    }

    if (inflight) inflight.abort();
    const controller = new AbortController();
    inflight = controller;
    root.classList.add('is-filtering');
    root.setAttribute('aria-busy', 'true');

    try {
      const markup = await fetchSection(url, sectionId);
      if (controller.signal.aborted) return;
      const page = new DOMParser().parseFromString(markup, 'text/html');
      const next = page.querySelector('results-list') || page.querySelector('.tsf-collection-results');
      if (!next) throw new Error('no results');

      const state = captureState(root);
      const currentFoot = root.parentElement?.querySelector('.tsf-pantry__foot');
      const nextFoot = page.querySelector('.tsf-pantry__foot');
      const currentCategories = root.parentElement?.querySelector('.tsf-pantry__category-grid');
      const nextCategories = page.querySelector('.tsf-pantry__category-grid');
      teardownRail();
      document.body.style.overflow = '';
      root.replaceChildren(...Array.from(next.childNodes).map((node) => document.importNode(node, true)));
      if (currentFoot && nextFoot) {
        currentFoot.replaceWith(document.importNode(nextFoot, true));
      }
      if (currentCategories && nextCategories) {
        currentCategories.replaceWith(document.importNode(nextCategories, true));
      }
      const nextDocumentTitle = next.getAttribute('data-document-title');
      if (nextDocumentTitle) document.title = nextDocumentTitle;
      // Initialization restores capped filter values from the URL. Publish the
      // response URL first so removed filters cannot be restored from old state.
      if (push) window.history.pushState({ tsfFacets: true }, '', url);
      init(root);
      restoreState(root, state);
      document.dispatchEvent(new CustomEvent('thinslim:facets-updated'));

      /* Bring the top of the results into view if the shopper had scrolled
         past it; otherwise leave the page exactly where it is. */
      const head = root.querySelector('.tsf-facets-head-slot');
      if (head) {
        const top = head.getBoundingClientRect().top;
        const offset = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-group-height')) || 0;
        if (top < offset) {
          head.scrollIntoView({
            block: 'start',
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
          });
        }
      }
    } catch (error) {
      if (error && error.name === 'AbortError') return;
      window.location.assign(url);
      return;
    } finally {
      if (inflight === controller) {
        inflight = null;
        root.classList.remove('is-filtering');
        root.removeAttribute('aria-busy');
      }
    }
  }

  let railTeardown = [];
  function teardownRail() {
    railTeardown.forEach((fn) => fn());
    railTeardown = [];
  }

  /* ------------------------------------------------------------ the rail */

  function initRail(rail) {
    const form = rail.querySelector('[data-facets-form]');
    if (!form) return;

    restoreCappedFilters(rail);
    initGroups(rail);

    const root = rail.closest('.tsf-collection-results');
    const openButton = document.querySelector('[data-facets-open]');
    const closeButton = rail.querySelector('[data-facets-close]');
    const backdrop = rail.querySelector('[data-facets-backdrop]');
    const panel = rail.querySelector('[data-facets-panel]');
    let lastFocused = null;
    let sheetSnapshot = null;

    /* The sheet only applies on Apply. Closing it any other way returns
       every option to what the results on screen were built from, so a
       ticked box never sits beside results it did not filter. */
    const sheetControls = () => Array.from(rail.querySelectorAll('input, select'))
      .filter((control) => !control.matches('[data-facet-search]') && control.type !== 'search');
    const snapshotSheet = () => sheetControls().map((control) => [
      control,
      control.type === 'checkbox' || control.type === 'radio' ? control.checked : control.value,
    ]);
    const restoreSheet = (snapshot) => {
      if (!snapshot) return;
      for (const [control, value] of snapshot) {
        if (!document.contains(control)) continue;
        if (control.type === 'checkbox' || control.type === 'radio') control.checked = value;
        else control.value = value;
      }
    };

    const apply = (url) => {
      if (root) {
        renderResults(root, url);
      } else {
        window.location.assign(url);
      }
    };

    /* Every submit, from Apply, from sort, or from the desktop auto-apply,
       goes through here, so the URL is always built the same clean way. */
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      const returnFocusToFilter = !DESKTOP.matches && rail.classList.contains('is-open');
      closeSheet(false);
      /* The sheet's Apply button becomes hidden. Focus the visible trigger;
         its stable id also lets renderResults restore focus after replacement. */
      if (returnFocusToFilter && openButton) openButton.focus({ preventScroll: true });
      apply(formUrl(form));
    });

    /* Desktop applies as soon as an option changes, after a short pause so
       ticking three boxes in a row costs one request, not three. The sheet
       waits for Apply, so a phone does not refresh once per tap. */
    form.addEventListener('change', (event) => {
      if (!DESKTOP.matches) return;
      if (event.target.matches('[data-facet-search]')) return;
      window.clearTimeout(pending);
      const wait = event.target.type === 'number' ? 0 : 250;
      pending = window.setTimeout(() => apply(formUrl(form)), wait);
    });

    const sort = document.querySelector('[data-facets-sort]');
    if (sort) {
      sort.addEventListener('change', () => {
        /* A mirrored collection (The Pantry, most reviewed) is another URL,
           not a sort_by value; its option carries the link. Active filters
           come along so the shopper keeps what they narrowed to. */
        const chosen = sort.options[sort.selectedIndex];
        if (chosen && chosen.hasAttribute('data-facets-link')) {
          const target = contextUrl(chosen.value);
          const current = new URL(formUrl(form), window.location.origin);
          current.searchParams.forEach((value, key) => {
            if (key.startsWith('filter.')) target.searchParams.append(key, value);
          });
          compatibleContext(target);
          // Do not leave a deferred form submit or the outgoing selection in
          // the history entry that the browser will restore on Back.
          window.clearTimeout(pending);
          restoreSortFromUrl();
          window.location.assign(target.href);
          return;
        }
        apply(formUrl(form));
      });
    }

    /* Prefetch the result an option would produce, on hover only, so touch
       devices and keyboard users never pay for requests they did not ask
       for. */
    const sectionId = root ? root.getAttribute('section-id') : null;
    if (sectionId && window.matchMedia('(hover: hover)').matches) {
      let hoverTimer = 0;
      form.addEventListener('pointerover', (event) => {
        const label = event.target.closest('.tsf-facet__option');
        if (!label) return;
        const input = document.getElementById(label.getAttribute('for'));
        if (!input || input.type !== 'checkbox') return;
        window.clearTimeout(hoverTimer);
        hoverTimer = window.setTimeout(() => {
          input.checked = !input.checked;
          const url = formUrl(form);
          input.checked = !input.checked;
          fetchSection(url, sectionId).catch(() => {});
        }, 80);
      });
    }

    /* Chips and Clear all links filter in place too. */
    if (root) {
      root.querySelectorAll('.tsf-chip').forEach((chip) => {
        chip.addEventListener('submit', (event) => {
          event.preventDefault();
          apply(formUrl(chip));
        });
      });
      root.querySelectorAll('.tsf-chips__clear, .tsf-facets__clear').forEach((link) => {
        link.addEventListener('click', (event) => {
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
          event.preventDefault();
          closeSheet(false);
          const target = contextUrl(link.getAttribute('href'));
          for (const key of [...target.searchParams.keys()]) {
            if (key.startsWith('filter.') || ['sort_by', 'brand', 'pairing'].includes(key)) target.searchParams.delete(key);
          }
          apply(target.pathname + target.search + target.hash);
        });
      });
    }

    /* ------------------------------------------------------- the sheet */

    function openSheet() {
      if (DESKTOP.matches) return;
      lastFocused = document.activeElement;
      rail.classList.add('is-open');
      rail.setAttribute('role', 'dialog');
      rail.setAttribute('aria-modal', 'true');
      if (backdrop) backdrop.hidden = false;
      document.body.style.overflow = 'hidden';
      if (openButton) openButton.setAttribute('aria-expanded', 'true');
      sheetSnapshot = snapshotSheet();
      const target = closeButton || panel;
      if (target) target.focus();
    }

    function dismissSheet(restoreFocus = true) {
      if (rail.classList.contains('is-open')) restoreSheet(sheetSnapshot);
      closeSheet(restoreFocus !== false);
    }

    function closeSheet(restoreFocus = true) {
      sheetSnapshot = null;
      rail.classList.remove('is-open');
      rail.removeAttribute('role');
      rail.removeAttribute('aria-modal');
      if (backdrop) backdrop.hidden = true;
      document.body.style.overflow = '';
      if (openButton) openButton.setAttribute('aria-expanded', 'false');
      if (restoreFocus && lastFocused && document.contains(lastFocused)) {
        lastFocused.focus();
      }
      lastFocused = null;
    }

    if (panel && !panel.hasAttribute('tabindex')) panel.setAttribute('tabindex', '-1');
    if (openButton) openButton.addEventListener('click', openSheet);
    if (closeButton) closeButton.addEventListener('click', dismissSheet);
    if (backdrop) backdrop.addEventListener('click', dismissSheet);

    rail.addEventListener('keydown', (event) => {
      if (!rail.classList.contains('is-open')) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        dismissSheet();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusable = Array.from(rail.querySelectorAll(focusableSelector)).filter(
        (element) => element.offsetParent !== null
      );
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    const onBreakpoint = (event) => {
      if (event.matches) dismissSheet(false);
    };
    DESKTOP.addEventListener('change', onBreakpoint);
    railTeardown.push(() => DESKTOP.removeEventListener('change', onBreakpoint));

    /* --------------------------------------- brand search and Show all */

    rail.querySelectorAll('[data-facet-searchable]').forEach((group) => {
      const search = group.querySelector('[data-facet-search]');
      const toggle = group.querySelector('[data-facet-toggle]');
      const toggleLabel = group.querySelector('[data-facet-toggle-label]');
      const status = group.querySelector('[data-facet-status]');
      const noun = group.getAttribute('data-facet-noun') || 'options';
      const options = Array.from(group.querySelectorAll('[data-facet-option]'));

      /* Match on the rendered text, not the attribute. The attribute carries
         whatever escaping the value arrived with, and a shopper types a plain
         apostrophe. */
      const searchKeys = new Map(
        options.map((option) => {
          const name = option.querySelector('.tsf-facet__name');
          const text = name ? name.textContent : option.getAttribute('data-facet-label');
          return [option, (text || '').trim().toLowerCase()];
        })
      );
      const collapsedLabel = toggleLabel ? toggleLabel.textContent.trim() : '';

      if (toggle) {
        toggle.addEventListener('click', () => {
          const expanded = group.classList.toggle('is-expanded');
          toggle.setAttribute('aria-expanded', String(expanded));
          if (toggleLabel) toggleLabel.textContent = expanded ? 'Show fewer' : collapsedLabel;
        });
      }

      if (!search) return;

      let announce;
      search.addEventListener('input', () => {
        const term = search.value.trim().toLowerCase();
        group.classList.toggle('is-searching', term.length > 0);

        let matches = 0;
        options.forEach((option) => {
          const label = searchKeys.get(option) || '';
          const hit = term === '' || label.includes(term);
          option.hidden = !hit;
          if (hit) matches += 1;
        });

        if (toggle) toggle.hidden = term.length > 0;

        if (!status) return;
        window.clearTimeout(announce);
        announce = window.setTimeout(() => {
          status.textContent =
            term === '' ? '' : `${matches} ${noun} match ${search.value.trim()}`;
        }, 300);
      });

      /* Enter in a search box would otherwise submit the filter form. */
      search.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') event.preventDefault();
      });
    });
  }

  /* -------------------------------------------------------- load more */

  function initLoadMore(root) {
    const link = root.querySelector('.tsf-pagination__more');
    if (!link) return;

    const grid = root.querySelector('[data-testid="product-grid"]');
    if (!grid) return;

    const button = document.createElement('button');
    button.type = 'button';
    button.className = link.className;
    button.textContent = 'Load more';
    let nextUrl = link.getAttribute('href');

    const status = document.createElement('p');
    status.className = 'visually-hidden';
    status.setAttribute('role', 'status');
    link.after(status);
    link.replaceWith(button);

    button.addEventListener('click', async () => {
      if (!nextUrl) return;
      button.disabled = true;
      button.textContent = 'Loading';

      try {
        const response = await fetch(nextUrl, { headers: { 'X-Requested-With': 'fetch' } });
        if (!response.ok) throw new Error(String(response.status));
        const markup = await response.text();
        const page = new DOMParser().parseFromString(markup, 'text/html');
        const nextGrid = page.querySelector('[data-testid="product-grid"]');
        if (!nextGrid) throw new Error('no grid');

        const added = Array.from(nextGrid.children);
        const firstAdded = added[0];
        added.forEach((card) => grid.append(card));

        const nextLink = page.querySelector('.tsf-pagination__more');
        nextUrl = nextLink ? nextLink.getAttribute('href') : null;

        const nextStatus = page.querySelector('.tsf-results-status');
        const currentStatus = root.querySelector('.tsf-results-status');
        if (nextStatus && currentStatus) currentStatus.innerHTML = nextStatus.innerHTML;

        const page_label = page.querySelector('.tsf-pagination__page');
        const currentPageLabel = root.querySelector('.tsf-pagination__page');
        if (page_label && currentPageLabel) currentPageLabel.textContent = page_label.textContent;

        status.textContent = `${added.length} more products loaded`;

        if (nextUrl) {
          button.disabled = false;
          button.textContent = 'Load more';
        } else {
          button.remove();
        }

        /* Keep the keyboard where the reader left off, on the first new card. */
        if (firstAdded) {
          const focusTarget = firstAdded.querySelector('a[href]');
          if (focusTarget) {
            focusTarget.setAttribute('tabindex', '-1');
            focusTarget.focus({ preventScroll: true });
          }
        }
      } catch (error) {
        button.disabled = false;
        button.textContent = 'Load more';
        status.textContent = 'Could not load more products. Try again.';
      }
    });
  }

  function init(scope) {
    scope.querySelectorAll('[data-tsf-facets]').forEach(initRail);
    const roots = Array.from(scope.querySelectorAll('.tsf-collection-results'));
    if (scope.matches && scope.matches('.tsf-collection-results')) roots.push(scope);
    roots.forEach(initLoadMore);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }

  function restoreSortFromUrl() {
    const sort = document.querySelector('[data-facets-sort]');
    if (!sort) return;
    const url = new URL(window.location.href);
    const linkedOption = [...sort.options].find(option =>
      option.hasAttribute('data-facets-link') &&
      new URL(option.value, url).pathname === url.pathname
    );
    const value = url.searchParams.get('sort_by') || linkedOption?.value || sort.dataset.defaultSort;
    const option = [...sort.options].find(option => option.value === value);
    if (option) sort.value = option.value;
  }

  // Browser page restoration can retain the outgoing mirrored-sort selection.
  // Restore the select from the current URL when returning from another route.
  window.addEventListener('pageshow', () => {
    // Native persisted form state is restored after pageshow. Reconcile once
    // that restoration has finished, using the URL as the source of truth.
    window.setTimeout(restoreSortFromUrl, 0);
  });

  /* Back and forward replay the filter state that URL describes. */
  window.addEventListener('popstate', () => {
    const root = document.querySelector('.tsf-collection-results[section-id]');
    if (root && root.querySelector('[data-tsf-facets]')) {
      renderResults(root, window.location.href, { push: false });
    }
  });

  /* The theme editor re-renders sections in place. */
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
